import React, { useCallback, useEffect, useReducer, useRef, useState } from "react"
import {
    atualizarConfiguracoesImpressora,
    buscarConfiguracoesImpressora,
} from "../../../services/configuracoes-impressora"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ParametrosGerais, TextoModel } from "../models/textoModel"
import { TextoImpressaoActionsTypes } from "./textoImpressaoActions"
import { TextoImpressaoContext } from "./TextoImpressaoContext"
import { ParametrosGeraisInicial, TextoImpressaoInicial } from "./textoImpressaoInicial"
import { getConfiguracoesDeTextoLocal, setConfiguracoesDeTextoLocal } from "./textoImpressaoLocal"
import { textoImpressaoReducer } from "./textoImpressaoReducer"

type textoImpressaoContextProviderProps = {
    children: React.ReactNode
}

type LayoutBackend = {
    id?: number
    parametrosGerais: ParametrosGerais
    campos: Omit<TextoModel, "id" | "parametrosGerais">
}

const { parametrosGerais: _pg, id: _id, ...camposIniciais } = TextoImpressaoInicial

function layoutParaModelo(layout: LayoutBackend): TextoModel {
    const pg = layout.parametrosGerais
    return {
        id: layout.id !== undefined ? String(layout.id) : undefined,
        parametrosGerais: {
            ...ParametrosGeraisInicial,
            ...pg,
            exibirLogo: Boolean(pg?.exibirLogo),
            linhaAposProduto: Boolean(pg?.linhaAposProduto),
        },
        ...camposIniciais,
        ...layout.campos,
    }
}

function modeloParaPayload(modelo: TextoModel): LayoutBackend {
    const { id, parametrosGerais, ...campos } = modelo
    const pg: ParametrosGerais = {
        ...parametrosGerais,
        exibirLogo: Boolean(parametrosGerais.exibirLogo),
        linhaAposProduto: Boolean(parametrosGerais.linhaAposProduto),
    }
    return id !== undefined
        ? { id: Number(id), parametrosGerais: pg, campos }
        : { parametrosGerais: pg, campos }
}

export function TextoImpressaoContextProvider({ children }: textoImpressaoContextProviderProps) {
    const [textoImpressao, dispatchTexoImpressao] = useReducer(
        textoImpressaoReducer,
        TextoImpressaoInicial
    )
    const [carregando, setCarregando] = useState(false)
    const localSnapshot = useRef(getConfiguracoesDeTextoLocal())
    const setLoading = useLoadingStore(s => s.setLoading)

    useEffect(() => {
        setConfiguracoesDeTextoLocal(textoImpressao)
    }, [textoImpressao])

    const carregarDoServidor = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            const response = await buscarConfiguracoesImpressora(autorizacao)

            if (!response.retornoErro && response?.campos) {
                dispatchTexoImpressao({
                    type: TextoImpressaoActionsTypes.INICIAL,
                    payload: layoutParaModelo(response),
                })
                return
            }

            dispatchTexoImpressao({
                type: TextoImpressaoActionsTypes.INICIAL,
                payload: localSnapshot.current,
            })
        } catch (erro) {
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregarDoServidor()
    }, [])

    const salvarConfiguracoes = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        setCarregando(true)
        try {
            const payload = modeloParaPayload(textoImpressao)
            const response = await atualizarConfiguracoesImpressora(payload, autorizacao)

            if (response.retornoErro) {
                throw new Error(response.mensagem)
            }

            if (response?.layout) {
                dispatchTexoImpressao({
                    type: TextoImpressaoActionsTypes.ATUALIZAR,
                    payload: layoutParaModelo(response.layout),
                })
            }
        } finally {
            setCarregando(false)
        }
    }, [textoImpressao])

    return (
        <TextoImpressaoContext.Provider
            value={{ textoImpressao, dispatchTexoImpressao, salvarConfiguracoes, carregando }}
        >
            <Loading />
            {children}
        </TextoImpressaoContext.Provider>
    )
}
